// covered-by: frontends/carrie/src/components/__tests__/Markdown.test.tsx

import { ArrowRight } from "@phosphor-icons/react";
import { HoverCard } from "@radix-ui/themes";
import { Link as RouterLink } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CHAT_CHIP_CLASS, ChatChipFace, ChatChipStatic } from "./ChatChipFace";

import { ASK_V2_CHAT_PATH } from "../../ask-v2/paths";
import {
  evidenceFocusKey,
  useEvidenceFocus,
  useEvidenceLitHandlers,
} from "../../utils/memoryCitationFocus";
import {
  SOURCE_HOVER_CLOSE_DELAY_MS,
  SOURCE_HOVER_OPEN_DELAY_MS,
} from "../ask-puntt-sources/SourceHoverCard";

/**
 * The chip a cited conversation leaves beside the claim it discussed.
 *
 * Hovering it tints that claim's wording and the matching rail card. The card
 * names the conversation and links to it.
 */
export function ChatCitationChip({
  chatId,
  title,
  number,
  scope,
  testId,
}: {
  chatId: string;
  title: string;
  number: number;
  scope: string;
  testId: string;
}) {
  const { t } = useTranslation("ui");
  const key = evidenceFocusKey("chat", scope, chatId);
  const { lit } = useEvidenceFocus(key);
  const litHandlers = useEvidenceLitHandlers(key);
  const [open, setOpen] = useState(false);
  const name = title || t("askV2.rail.citedChatUntitled");
  const suffix = `${scope}-${chatId}`;

  return (
    <span className="ml-1 inline-flex align-middle">
      <HoverCard.Root
        open={open}
        onOpenChange={setOpen}
        openDelay={SOURCE_HOVER_OPEN_DELAY_MS}
        closeDelay={SOURCE_HOVER_CLOSE_DELAY_MS}
      >
        <HoverCard.Trigger>
          <button
            {...litHandlers}
            type="button"
            data-testid={testId || `chat-citation-chip-${suffix}`}
            data-lit={lit ? "true" : "false"}
            aria-label={t("askPuntt.chatCitation.chipAria", {
              n: number,
              title: name,
            })}
            onClick={() => setOpen(true)}
            className={CHAT_CHIP_CLASS}
          >
            <ChatChipFace number={number} />
          </button>
        </HoverCard.Trigger>
        <HoverCard.Content
          side="top"
          data-testid={`chat-citation-popover-${suffix}`}
          className="max-h-[min(70vh,420px)] w-[280px] overflow-y-auto rounded-2xl border border-puntt-neutral-gray-6 bg-base-white p-4 text-left shadow-lg"
        >
          <div
            role="group"
            aria-label={t("askPuntt.chatCitation.popoverAria", { n: number })}
          >
            <div className="mb-2 flex items-center gap-2">
              <ChatChipStatic number={number} />
              <span className="min-w-0 text-[13px] font-semibold text-puntt-neutral-gray-12">
                {name}
              </span>
            </div>
            <RouterLink
              to={ASK_V2_CHAT_PATH}
              params={{ chatId }}
              data-testid={`chat-citation-open-${suffix}`}
              className="inline-flex items-center gap-1 text-[12px] font-semibold leading-4 text-[color:var(--puntt-link-chat)] no-underline hover:underline"
            >
              {t("askPuntt.chatCitation.openChat")}
              <ArrowRight size={12} aria-hidden="true" />
            </RouterLink>
          </div>
        </HoverCard.Content>
      </HoverCard.Root>
    </span>
  );
}
